import { useMemo, useState } from 'react'

import { decode, encode } from 'js-base64'

import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  CustomFieldType,
  CustomFieldValidators,
  MassActionRequest,
  MassActionRequestAction,
  MassActionRequestContext,
  Member,
  MemberEmailStatus,
  MemberStatus,
  MemberStatusInput,
  RoleType,
  StaffReason,
} from '@tribeplatform/gql-client/types'
import { CustomFieldSubtype } from '@tribeplatform/react-components/CustomField'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  getTranslatedRoleName,
  getTranslatedStaffReason,
  UserImportModal,
} from '@tribeplatform/react-components/UserImport'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useAuthToken,
  useCreateExportMemberRequest,
  useCreateMassActionRequest,
  useMembers,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { IconButton, Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link as LinkComponent } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { useQuery } from '../../../hooks/useQuery.js'
import {
  getTranslatedEmailStatus,
  getTranslatedMemberStatus,
} from '../../../utils/members.utils.js'
import { useDebounce } from '../../../utils/useDebounce.js'
import { MemberActionItems } from '../../Member/MemberActionItems.js'
import { Table } from '../../Table/index.js'
import { tableColumnsFromCustomFields } from '../../Table/utils.js'
import { ConfirmMassActionModal } from '../MassActions/ConfirmMassActionModal.js'
import { MassActionBanner } from '../MassActions/MassActionBanner.js'
import { MassActionModal } from '../MassActions/MassActionModal.js'
import { MassActionModalState } from '../MassActions/types.js'

import { ExportWizardModal } from './ExportWizardModal.js'
import { sanitizeMemberFilters } from './filters-sanitizer.js'

export const Members = () => {
  const intl = useI18n()
  const { $t } = intl
  let initialState = {
    orderBy: {
      column: 'createdAt',
      reverse: true,
    },
    query: null,
    filterBy: [],
  }

  const { replace } = useRouter()
  const { state: queryState } = useQuery()
  if (queryState) {
    try {
      initialState = { ...initialState, ...JSON.parse(decode(queryState)) }
    } catch (error) {
      logger.error('Could not parse initial state for members')
    }
  }
  const { mutateAsync: createMassActionRequest } = useCreateMassActionRequest()
  const [state, setState] = useState(initialState)

  const [inviteMembersModal, setInviteMembersModal] = useState(false)
  const [exportModal, setExportModal] = useState<{
    selectedFields: unknown
    allFields: unknown
  }>()
  const [massActionModalState, setMassActionModalState] =
    useState<MassActionModalState>({ type: 'closed' })
  const [massActionRequest, setMassActionRequest] =
    useState<MassActionRequest>(null)

  const {
    data: { network },
  } = useAuthToken()
  const debouncedQuery = useDebounce(state?.query, 300)

  const { data: totalMembersData } = useMembers({
    fields: 'basic',
    variables: {
      limit: 0,
      status: [
        MemberStatusInput.Suspended,
        MemberStatusInput.VERIFIED,
        MemberStatusInput.UNVERIFIED,
      ],
    },
  })

  const sanitizedFilterBy = sanitizeMemberFilters(
    state?.filterBy ?? [],
    network?.roles ?? [],
  )
  const {
    data,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useMembers({
    fields: {
      profilePicture: 'basic',
      banner: 'basic',
      fields: 'all',
      role: 'basic',
      authMemberProps: 'all',
    },
    variables: {
      limit: 15,
      query: debouncedQuery,
      orderBy: state?.orderBy.column,
      reverse: state?.orderBy.reverse,
      status: [
        MemberStatusInput.Suspended,
        MemberStatusInput.VERIFIED,
        MemberStatusInput.UNVERIFIED,
      ],
      filterBy: sanitizedFilterBy,
    },
    useInfiniteQueryOptions: { keepPreviousData: true },
  })

  const totalMembers = totalMembersData?.pages?.[0]?.totalCount

  const { mutateAsync: createExportRequest } = useCreateExportMemberRequest()

  const members = simplifyPaginatedResult<Member>(data)
  members?.nodes?.forEach(member => {
    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    member.mappedFields = member?.fields?.reduce((result, cur) => {
      try {
        result[cur.key] = JSON.parse(cur.value)
      } catch (e) {
        logger.error('Error parsing member field value', e.message)
      }
      return result
    }, {})
  })

  const handleStateChange = (newState: typeof state) => {
    setState(newState)
    replace(`?state=${encode(JSON.stringify(newState))}`)
  }

  const handleSelectMassAction = async (action: MassActionRequestAction) => {
    if (
      action === MassActionRequestAction.Delete ||
      action === MassActionRequestAction.Suspend ||
      action === MassActionRequestAction.Flag
    ) {
      try {
        const massActionRequest = await createMassActionRequest({
          input: {
            context: MassActionRequestContext.Member,
            action,
            waitForConfirmation: true,
            filters: {
              query: state?.query,
              filterBy: sanitizedFilterBy,
            },
          },
        })
        setMassActionModalState({
          type: 'confirm',
          massActionRequest,
        })
      } catch (e) {
        const errors = (e as ClientError)?.response?.errors
        toast({
          title:
            errors?.[0]?.message ??
            $t({
              defaultMessage: 'Unable to perform mass action',
              id: 'MassAction.Failed',
            }),
          status: 'error',
        })
      }
    } else {
      setMassActionModalState({ type: 'immediate', action })
    }
  }

  const onExport = async fields => {
    try {
      await createExportRequest({
        input: {
          fields,
          query: state?.query,
          filterBy: sanitizedFilterBy,
        },
      })
      toast({
        title: $t({ defaultMessage: 'In Progress', id: 'Generics.InProgress' }),
        description: $t({
          defaultMessage: 'Download link will be emailed to you soon',
          id: 'Admin.Members.ExportInProgress.Description',
        }),
        status: 'info',
        icon: <SvgIcon name="info" />,
      })
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Unable to export members',
          id: 'Admin.Members.FailedToExport',
        }),
        status: 'error',
      })
    }
  }

  const customColumns = useMemo(() => {
    return tableColumnsFromCustomFields(network?.memberFields?.fields)
  }, [network?.memberFields?.fields])

  const columns = useMemo(
    () => [
      {
        name: $t({
          defaultMessage: 'Profile picture',
          id: 'Generics.ProfilePicture',
        }),
        Header: (
          <div className="w-8">
            <SvgIcon className="w-5 h-5 m-auto" name="image" />
          </div>
        ),
        accessor: 'profilePicture.url',
        renderer: 'avatar',
        visible: true,
        width: 'collapse',
      },
      {
        Header: $t({ defaultMessage: 'ID', id: 'Generics.ID' }),
        accessor: 'id',
        renderer: 'memberPrimary',
        visible: false,
        width: 'sm',
      },
      {
        Header: $t({ defaultMessage: 'Name', id: 'Generics.Name.Noun' }),
        accessor: 'name',
        renderer: 'memberPrimary',
        visible: true,
        width: 'md',
        type: CustomFieldType.text,
      },
      {
        Header: $t({ defaultMessage: 'Username', id: 'Generics.Username' }),
        accessor: 'username',
        renderer: 'memberPrimary',
        width: 'md',
        visible: true,
        type: CustomFieldType.text,
      },
      {
        Header: $t({ defaultMessage: 'Email', id: 'Generics.Email' }),
        accessor: 'email',
        width: 'lg',
        visible: true,
        type: CustomFieldType.text,
        subtype: CustomFieldSubtype.EMAIL,
        renderer: (_: unknown, member: Member) => {
          return (
            <LinkComponent
              href={`mailto:${member?.email}`}
              target="_blank"
              variant="neutral"
            >
              <span className="inline-flex space-s-1 items-center">
                <span>{member?.email}</span>
                {member?.emailStatus !== MemberEmailStatus.verified && (
                  <Tooltip>
                    <Tooltip.Trigger>
                      <SvgIcon
                        className="w-5 h-5 text-content-on-attention"
                        name="status-warning"
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Panel>
                      {$t({
                        defaultMessage: 'Unverified email',
                        id: 'Generics.UnverifiedEmail',
                      })}
                    </Tooltip.Panel>
                  </Tooltip>
                )}
              </span>
            </LinkComponent>
          )
        },
      },
      {
        Header: $t({ defaultMessage: 'Tagline', id: 'Generics.Tagline' }),
        accessor: 'tagline',
        width: 'md',
        visible: false,
      },
      {
        Header: $t({
          defaultMessage: 'Site role',
          id: 'Generics.CommunityRole',
        }),
        accessor: 'role.name',
        width: 'sm',
        visible: false,
        subtype: CustomFieldSubtype.SELECT,
        type: CustomFieldType.text,
        exact: true,
        validators: [
          {
            validation: CustomFieldValidators.enum,
            value: JSON.stringify(
              network?.roles
                ?.filter(role => role?.type !== RoleType.guest)
                ?.map(role => role.name),
            ),
          },
        ],
        labels: network?.roles?.reduce((translatedLabels, role) => {
          return {
            ...translatedLabels,
            [role.name]: getTranslatedRoleName(intl, role.name),
          }
        }, {}),
        renderer: (_: unknown, member: Member) => {
          return getTranslatedRoleName(intl, member.role.name)
        },
      },
      {
        Header: $t({
          defaultMessage: 'Seat usage note',
          id: 'Generics.SeatUsageNote',
        }),
        accessor: 'staffReasons',
        width: 'md',
        subtype: CustomFieldSubtype.SELECT,
        type: CustomFieldType.text,
        visible: true,
        renderer: (_: unknown, member: Member) => {
          const reasonNames =
            member?.staffReasons.map(reason =>
              getTranslatedStaffReason(intl, reason),
            ) ?? []
          const reasons = intl.formatList(reasonNames, { style: 'narrow' })

          if (reasonNames.length < 2) {
            return reasons
          }
          return (
            <Tooltip placement="top">
              <Tooltip.Trigger>
                <span className="inline-block truncate w-full">{reasons}</span>
              </Tooltip.Trigger>
              <Tooltip.Panel>{reasons}</Tooltip.Panel>
            </Tooltip>
          )
        },
        validators: [
          {
            validation: CustomFieldValidators.enum,
            value: JSON.stringify(Object.values(StaffReason)),
          },
        ],
        labels: {
          [StaffReason.NetworkStaffRole]: getTranslatedStaffReason(
            intl,
            StaffReason.NetworkStaffRole,
          ),
          [StaffReason.SpaceStaffRole]: getTranslatedStaffReason(
            intl,
            StaffReason.SpaceStaffRole,
          ),
          [StaffReason.SamlSso]: getTranslatedStaffReason(
            intl,
            StaffReason.SamlSso,
          ),
        },
      },
      {
        Header: $t({ defaultMessage: 'Flagged', id: 'Generics.Flagged' }),
        accessor: 'flagged',
        width: 'sm',
        visible: false,
        renderer: 'boolean',
        type: CustomFieldType.boolean,
        extraProps: {
          bare: true,
        },
      },
      {
        Header: $t({ defaultMessage: 'Created at', id: 'Generics.CreatedAt' }),
        accessor: 'createdAt',
        renderer: 'date',
        orderKey: 'createdAt',
        width: 'sm',
        visible: true,
        type: CustomFieldType.date,
      },
      {
        Header: $t({ defaultMessage: 'Updated at', id: 'Generics.UpdatedAt' }),
        accessor: 'updatedAt',
        orderKey: 'updatedAt',
        renderer: 'date',
        width: 'sm',
        visible: true,
        type: CustomFieldType.date,
      },
      {
        Header: $t({ defaultMessage: 'Last seen', id: 'Generics.LastSeen' }),
        accessor: 'lastSeenAt',
        orderKey: 'lastSeenAt',
        renderer: 'date',
        format: 'fromNow',
        type: CustomFieldType.date,
        width: 'sm',
        visible: false,
      },
      {
        Header: $t({ defaultMessage: 'Status', id: 'Generics.Status' }),
        accessor: 'status',
        width: 'sm',
        subtype: CustomFieldSubtype.SELECT,
        type: CustomFieldType.text,
        validators: [
          {
            validation: CustomFieldValidators.enum,
            // Our values in filter by should match the ones in the database not the enum ones
            // This is a temporary solution until we fix the enum names tech debt in the back-end
            value: JSON.stringify([
              MemberStatus.VERIFIED,
              MemberStatus.UNVERIFIED,
              MemberStatus.Suspended.toUpperCase(),
            ]),
          },
        ],
        labels: {
          [MemberStatus.VERIFIED]: getTranslatedMemberStatus(
            intl,
            MemberStatus.VERIFIED,
          ),
          [MemberStatus.UNVERIFIED]: getTranslatedMemberStatus(
            intl,
            MemberStatus.UNVERIFIED,
          ),
          [MemberStatus.Suspended.toUpperCase()]: getTranslatedMemberStatus(
            intl,
            MemberStatus.Suspended,
          ),
        },
        renderer: (_: unknown, member: Member) => {
          return getTranslatedMemberStatus(intl, member.status)
        },
      },
      {
        Header: $t({
          defaultMessage: 'Email status',
          id: 'Generics.EmailStatus',
        }),
        accessor: 'emailStatus',
        width: 'sm',
        renderer: (_: unknown, member: Member) => {
          return getTranslatedEmailStatus(intl, member.emailStatus)
        },
      },
      {
        Header: $t({ defaultMessage: 'Locale', id: 'Generics.Locale' }),
        accessor: 'locale',
        width: 'sm',
      },
      ...customColumns,
      {
        visible: true,
        accessor: 'actions',
        locked: true,
        width: 'collapse',
        renderer: (_: unknown, member: Member) => (
          <Dropdown placement="bottom-end">
            <Dropdown.ButtonMinimal as="div">
              <IconButton
                variant="tertiaryNeutral"
                rounded
                icon={<SvgIcon name="dots-horizontal" />}
                size="sm"
              />
            </Dropdown.ButtonMinimal>
            <MemberActionItems member={member} context="admin" />
          </Dropdown>
        ),
      },
    ],
    [$t, customColumns, network?.roles, intl],
  )

  return (
    <div className="w-full flex flex-col gap-3">
      <MassActionBanner massActionRequest={massActionRequest} />

      <Table
        storageId="members"
        isLoading={isInitialLoading}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        columns={columns}
        data={data}
        customTotalCount={totalMembers}
        state={state}
        onStateChange={handleStateChange}
        title={$t({ defaultMessage: '{MEMBERS_CC}', id: 'Generics.Members' })}
        keyboardControl
        entityType="member"
        massActions={[
          MassActionRequestAction.Suspend,
          MassActionRequestAction.Delete,
          MassActionRequestAction.AddToSpace,
          MassActionRequestAction.AssignBadge,
          MassActionRequestAction.RevokeBadge,
          MassActionRequestAction.Flag,
          MassActionRequestAction.Unflag,
        ]}
        onMassAction={handleSelectMassAction}
        massActionContext={MassActionRequestContext.Member}
        onExport={(allColumns, visibleColumns) => {
          const selectedFields = visibleColumns?.map(field =>
            field.id
              .replace('mappedFields', 'fields')
              .replace('role.name', 'role'),
          )
          const allFields = allColumns?.map(field =>
            field.accessor
              .replace('mappedFields', 'fields')
              .replace('role.name', 'role'),
          )
          setExportModal({ selectedFields, allFields })
        }}
        actions={
          <div className="flex space-s-3">
            <Button
              size="lg"
              variant="primary"
              onClick={() => setInviteMembersModal(true)}
            >
              <T defaultMessage="Invite {MEMBERS}" id="Admin.Members.Invite" />
            </Button>
          </div>
        }
      />
      {inviteMembersModal ? (
        <UserImportModal open onClose={() => setInviteMembersModal(false)} />
      ) : null}
      {!!exportModal && (
        <ExportWizardModal
          open={!!exportModal}
          onClose={() => setExportModal(undefined)}
          title={$t({
            defaultMessage: 'Exporting {MEMBERS}',
            id: 'Admin.Members.ExportModal.Title',
          })}
          description={$t({
            defaultMessage:
              'A csv file containing the {MEMBERS} will be generated that includes the fields and filters you selected on this page. Once the file is ready to be downloaded you will get an email containing the download link.',
            id: 'Admin.Members.ExportModal.Description',
          })}
          onExport={onExport}
          selectedFields={exportModal?.selectedFields}
          allFields={exportModal?.allFields}
        />
      )}
      {massActionModalState.type === 'confirm' && (
        <ConfirmMassActionModal
          open={massActionModalState.type === 'confirm'}
          onClose={() => setMassActionModalState({ type: 'closed' })}
          onSuccess={setMassActionRequest}
          massActionRequest={massActionModalState.massActionRequest}
        />
      )}
      {massActionModalState.type === 'immediate' && (
        <MassActionModal
          open={massActionModalState.type === 'immediate'}
          onClose={() => setMassActionModalState({ type: 'closed' })}
          context={MassActionRequestContext.Member}
          action={massActionModalState.action}
          count={data?.pages?.[0]?.totalCount}
          onSuccess={setMassActionRequest}
          filterBy={sanitizedFilterBy}
          query={state?.query}
        />
      )}
    </div>
  )
}
